import React, { useContext, useState, useEffect } from 'react';
import { StoreContext } from '../context/StoreContext';
import { FaStar } from 'react-icons/fa';
import { FaMinus } from "react-icons/fa6";
import { FaPlus } from "react-icons/fa6";
import { toast } from 'react-toastify';
import ItemCustomizationBottomSheet from './ItemCustomizationBottomSheet';

const RESTAURANT_CLOSED_TOAST_ID = 'restaurant-closed';

function FoodCard({
    name,
    price,
    rating,
    ratingCounts,
    thumbnailUrl,
    dishId,
    restaurantId,
    roadId,
    availableNow,
    variations = [],
    addOnGroups = [],
    description,
    foodPreference,
    defaultPriciestVariant = false,
    restaurantClosed = false
}) {
    const { prevRestaurantId, cartData, addToCart, removeFromCart, restaurantData, setIsBottomSheetOpen, clearCart } = useContext(StoreContext);
    const [showConfirm, setShowConfirm] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const currentRestaurant = restaurantData?.find(restaurant=>restaurant.restaurantId === restaurantId);
    const [quantity, setQuantity] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    // Bottom sheet state
    const [showCustomizationSheet, setShowCustomizationSheet] = useState(false);

    // Check if this item has variants
    const hasVariants = variations && variations.length > 0;

    // Check if dish has add-ons (for basic dishes)
    const hasAddOns = !hasVariants && addOnGroups && addOnGroups.length > 0;

    // Check if any variant has add-ons
    const hasVariantAddOns = hasVariants && variations.some(variant =>
        variant.addOnGroups && variant.addOnGroups.length > 0
    );

    // Check if bottom sheet should be shown
    const shouldShowBottomSheet = hasVariants || hasAddOns;

    useEffect(() => {
        if (hasVariants) {
            // For items with variants, calculate total quantity across all variants
            const dishCartData = cartData[dishId];
            let totalQuantity = 0;
            
            if (typeof dishCartData === 'object' && dishCartData !== null) {
                // New v3 structure: {dishId: {variantId: {count, addOnItems}}}
                totalQuantity = Object.values(dishCartData).reduce((sum, variantData) => {
                    if (typeof variantData === 'number') {
                        // Handle legacy format
                        return sum + variantData;
                    } else {
                        // Handle new format
                        return sum + (variantData.count || 0);
                    }
                }, 0);
            } else if (typeof dishCartData === 'number') {
                // Fallback for old structure
                totalQuantity = dishCartData;
            }
            
            setQuantity(totalQuantity);
        } else {
            // For items without variants, use dishId directly
            const dishCartData = cartData[dishId];
            if (typeof dishCartData === 'number') {
                setQuantity(dishCartData);
            } else if (dishCartData && dishCartData.count !== undefined) {
                setQuantity(dishCartData.count);
            } else {
                setQuantity(0);
            }
        }
    }, [cartData, dishId, hasVariants, variations]);

    const handleAddToCart = async () => {
        if (restaurantClosed) {
            if (!toast.isActive(RESTAURANT_CLOSED_TOAST_ID)) {
                toast.error('This restaurant is currently closed. Ordering will resume once it reopens.', {
                    toastId: RESTAURANT_CLOSED_TOAST_ID,
                    position: 'top-center',
                    autoClose: 3000
                });
            }
            return;
        }

        if (!availableNow) return;

        if (prevRestaurantId && prevRestaurantId !== restaurantId) {
            setPendingAction({
                type: shouldShowBottomSheet ? 'openSheet' : 'addDirect'
            });
            setShowConfirm(true);
            return;
        }

        if (shouldShowBottomSheet) {
            // Show customization bottom sheet for variants or add-ons
            setShowCustomizationSheet(true);
            setIsBottomSheetOpen(true);
            return;
        }

        // No variants or add-ons, add directly to cart
        setIsLoading(true);
        try {
            await addToCart(dishId, restaurantId, roadId);
        } catch (error) {
            console.error("Error adding to cart:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRemoveFromCart = async () => {
        setIsLoading(true);
        try {
            if (hasVariants) {
                // Clear all variants for this dish so user can re-add from scratch
                await removeFromCart(dishId, null, true);
            } else {
                await removeFromCart(dishId);
            }
        } catch (error) {
            console.error("Error removing from cart:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCustomizationSheetAddToCart = async (selectedVariant, quantity, addOnItems) => {
        setIsLoading(true);
        try {
            if (selectedVariant) {
                // Add variant with add-ons
                await addToCart(
                    dishId,
                    restaurantId,
                    roadId,
                    selectedVariant.variationId,
                    addOnItems,
                    quantity
                );
            } else {
                // Add basic dish with add-ons
                await addToCart(
                    dishId,
                    restaurantId,
                    roadId,
                    null,
                    addOnItems,
                    quantity
                );
            }
            setShowCustomizationSheet(false);
            setIsBottomSheetOpen(false);
        } catch (error) {
            console.error("Error adding to cart:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const confirmRemovePreviousItems = async () => {
        try {
            await clearCart();
        } catch (error) {
            console.log(error);
        }
        const action = pendingAction;
        setPendingAction(null);
        setShowConfirm(false);

        if (!action) return;
        if (action.type === 'openSheet') {
            setShowCustomizationSheet(true);
            setIsBottomSheetOpen(true);
            return;
        }

        setIsLoading(true);
        try {
            await addToCart(dishId, restaurantId, roadId);
        } catch (error) {
            console.error("Error adding to cart:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const cancelAction = () => {
        setPendingAction(null);
        setShowConfirm(false);
    };

    const formatPrice = (price) => {
        return `₹${(price / 100).toFixed(2)}`;
    };

    const hasImage = Boolean(thumbnailUrl);

    const renderFoodPreference = () => {
        if (!foodPreference) return null;
        const isVeg = foodPreference?.toUpperCase() === 'VEG';
        const border = isVeg ? 'border-green-600' : 'border-red-600';
        const dot = isVeg ? 'bg-green-600' : 'bg-red-600';
        return (
            <span className={`inline-flex items-center justify-center w-4 h-4 border ${border} rounded-sm`}
                title={isVeg ? 'Vegetarian' : 'Non-Vegetarian'}
            >
                <span className={`w-2 h-2 rounded-full ${dot}`}></span>
            </span>
        );
    };

    const renderRatingBadge = () => {
        if (!rating || rating <= 0) return null;
        return (
            <div className='inline-flex items-center gap-1 px-2 py-1 rounded-md border border-green-200 bg-green-50 text-xs font-semibold text-green-700'>
                <FaStar className='text-[0.65rem]' />
                <span>{rating.toFixed(1)}</span>
            </div>
        );
    };
 
    return (
        <div className='w-full flex flex-col'>
            <div className={`flex justify-between gap-5 ${hasImage ? 'py-6' : 'py-4'} border-b border-gray-100`}>
                {/* Main item row */}
                <div className="flex flex-col flex-1 gap-2">
                    <div className='flex items-center gap-2'>
                        {renderFoodPreference()}
                        <h2 className="font-semibold text-lg md:text-xl text-[#262626]">{name}</h2>
                    </div>

                    <div className='flex items-center gap-3 text-sm md:text-base text-[#3e4152]'>
                        <p className="font-semibold">{formatPrice(price)}</p>
                        {renderRatingBadge()}
                        {ratingCounts > 0 && (
                            <span className='text-xs text-gray-500 font-medium'>({ratingCounts})</span>
                        )}
                    </div>

                    {description && (
                        <p className='text-sm text-gray-500 leading-relaxed max-w-2xl overflow-hidden'
                            style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
                        >
                            {description}
                        </p>
                    )}

                    {!availableNow && (
                        <p className="text-sm font-medium text-red-500">Currently Unavailable</p>
                    )}

                    {(hasAddOns || hasVariantAddOns) && (
                        <p className="text-xs uppercase tracking-wide text-blue-600 font-semibold">Customisable</p>
                    )}
                </div>

                {/* Button container - aligned with Swiggy style */}
                <div className={`relative flex flex-col items-center ${hasImage ? 'justify-end w-[110px] md:w-[128px]' : 'justify-center w-[110px]'}`}>
                    {hasImage && (
                        <div className='relative w-full h-[110px] md:h-[128px] rounded-xl overflow-hidden'>
                            <img
                                src={thumbnailUrl}
                                alt={name}
                                className='absolute inset-0 w-full h-full object-cover'
                                onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                }}
                            />
                        </div>
                    )}

                    <div className={`relative ${hasImage ? '-mt-5' : ''}`}>
                        {quantity > 0 ? (
                            <div className='bg-white text-[#3e4152] py-2.5 px-3 rounded-lg border border-gray-200 flex items-center gap-3 shadow-md min-w-[110px] justify-between'>
                                <button
                                    onClick={handleRemoveFromCart}
                                    disabled={isLoading}
                                    className={`${isLoading ? 'opacity-50 cursor-not-allowed' : ''} flex items-center justify-center w-10 h-10 rounded-full border border-gray-300`}
                                >
                                    <FaMinus className='text-base' />
                                </button>
                                <span className='text-base font-semibold text-center min-w-[24px]'>{quantity}</span>
                                <button
                                    onClick={handleAddToCart}
                                    disabled={isLoading || !availableNow || restaurantClosed}
                                    className={`${isLoading || !availableNow || restaurantClosed ? 'opacity-50 cursor-not-allowed' : ''} flex items-center justify-center w-10 h-10 rounded-full border border-gray-300`}
                                >
                                    <FaPlus className='text-base' />
                                </button>
                            </div>
                        ) : (
                            <button
                                className={`bg-white text-[#3e4152] text-base font-semibold py-2.5 px-6 rounded-lg border border-gray-300 shadow-md min-w-[110px] min-h-[46px] ${isLoading || !availableNow || restaurantClosed ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                                onClick={handleAddToCart}
                                disabled={isLoading || !availableNow || restaurantClosed}
                            >
                                ADD
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Item Customization Bottom Sheet */}
            <ItemCustomizationBottomSheet
                isOpen={showCustomizationSheet}
                onClose={() => {
                    setShowCustomizationSheet(false);
                    setIsBottomSheetOpen(false);
                }}
                dish={{
                    dishId,
                    name,
                    price,
                    addOnGroups
                }}
                variants={variations}
                onAddToCart={handleCustomizationSheetAddToCart}
                isLoading={isLoading}
                defaultPriciestVariant={defaultPriciestVariant}
            />

            {/* Custom confirmation modal */}
            {showConfirm && (
                <div className='fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 flex items-center justify-center z-50'>
                    <div className='bg-white p-6 rounded-lg max-w-sm w-full'>
                        <p className='font-semibold text-lg'>Start a new cart?</p>
                        <p className='text-sm text-gray-600 mt-2'>
                            Your cart has items from another restaurant. We’ll clear them before adding this item.
                        </p>
                        <div className='flex justify-end mt-4'>
                            <button className='bg-red-500 text-white px-4 py-2 rounded mr-2' onClick={confirmRemovePreviousItems}>
                                Yes
                            </button>
                            <button className='bg-gray-300 text-black px-4 py-2 rounded' onClick={cancelAction}>
                                No
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default FoodCard;
